import { InvestmentTransaction, TransactionSource } from '../../../types/investmentApi.types'
import {
  convertEpochToDate,
  convertUnitAndValue,
  fixedDigitsAndFormatNumberWithCurrency,
  formatNumberWithoutCurrency,
  toFixed,
} from '../../../../../utils/utils'
import { TransactionTypes } from '../../../types/investmentApi.types'
import RenderHTML from '../../../../../components/ui-components/RenderHTML'
import { GROUPED_STATIC_TEXT_ROLE } from '../../../../../components/ui-components/NameValueTableRow'

type TransactionPropTypes = {
  transaction: InvestmentTransaction
}

const getTransactionTypeText = (type: `${TransactionTypes}`) => {
  switch (type) {
    case TransactionTypes.BUY:
      return 'Buy'
    case TransactionTypes.SELL:
      return 'Sell'
    case TransactionTypes.SIP:
      return 'SIP'
    default:
      return ''
  }
}

const getSourceLabel = (source: `${TransactionSource}` | undefined) => {
  switch (source) {
    case TransactionSource.EXTERNAL:
      return 'External'
    case TransactionSource.POWER_UP:
      return 'PowerUp'
    case TransactionSource.INFINITE:
      return 'Infinite'
    default:
      return ''
  }
}

function buildTransactionLabel(transaction: InvestmentTransaction): string {
  const type = getTransactionTypeText(transaction?.type)
  const source = getSourceLabel(transaction?.source)
  const units = formatNumberWithoutCurrency(+toFixed(transaction?.units, 2))
  const nav = convertUnitAndValue({
    value: transaction?.nav?.amount,
    unit: transaction?.nav?.unit
  })
  const amount = fixedDigitsAndFormatNumberWithCurrency(transaction?.txnAmount?.amount, 2)
  const date = convertEpochToDate(transaction?.timestamp ?? Date.now(), '%_d %b %Y')

  return [source, type, `${units} units`, `NAV: ${nav}`, amount, date].filter(Boolean).join(', ')
}

export default function TransactionRow({ transaction }: TransactionPropTypes) {
  // Transaction History_B05/B06/B07 (iOS): role=text exposes one VoiceOver-focusable
  // AXStaticText (plain aria-label + aria-hidden children alone does not receive focus in WKWebView).
  const cardLabel = buildTransactionLabel(transaction)

  return (
    <div
      role={GROUPED_STATIC_TEXT_ROLE}
      aria-label={cardLabel}
      className='flex flex-col'
    >
      <div aria-hidden='true' className='flex flex-col'>
        <div className='flex items-center gap-1 pt-1 pl-[52px] pb-1'>
          {transaction?.source === TransactionSource.EXTERNAL && (
            <>
              <div aria-hidden='true'>
                <RenderHTML innerHTMLText={ExternalIcon} />
              </div>
              <div className='text-[12px] font-[400] text-[#6A6E6E]'>External</div>
            </>
          )}
          {transaction?.source === TransactionSource.POWER_UP && (
            <>
              <div aria-hidden='true'>
                <RenderHTML innerHTMLText={PowerUpIcon} />
              </div>
              <div className='text-[12px] font-[400] text-[#6A6E6E]'>PowerUp</div>
            </>
          )}
          {transaction?.source === TransactionSource.INFINITE && (
            <div className='text-[12px] font-[400] text-[#6A6E6E]'>Infinite</div>
          )}
        </div>
        <div className='flex justify-between'>
          <div className='flex gap-4 items-center'>
            <div
              aria-hidden='true'
              className={`rounded-[8px] w-[37px] h-[21px] text-13 font-[500] flex items-center justify-center
          ${transaction?.type === TransactionTypes.BUY || transaction?.type === TransactionTypes.SIP ? 'text-[#3E6CE3] font-[500] border border-[#E6F2FF] pb-[1px]' : 'text-[#CC5C2D] border border-[#FFF4EB]'}`}
            >
              {getTransactionTypeText(transaction?.type)}
            </div>
            <div className='flex flex-col '>
              <div className='text-black font-[500] leading-[130%]'>
                {formatNumberWithoutCurrency(+toFixed(transaction?.units, 2))} units
              </div>
              <div className='text-[#6A6E6E] text-13'>
                NAV:{' '}
                {convertUnitAndValue({
                  value: transaction?.nav?.amount,
                  unit: transaction?.nav?.unit
                })}
              </div>
            </div>
          </div>
          <div className='flex flex-col items-end'>
            <div className='text-black font-[500] leading-[130%]'>
              {fixedDigitsAndFormatNumberWithCurrency(transaction?.txnAmount?.amount, 2)}
            </div>
            <div className='text-[#6A6E6E] text-13'>
              {convertEpochToDate(transaction?.timestamp ?? Date.now(), '%_d %b %Y')}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const ExternalIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
  <rect width="12" height="12" fill="white"/>
  <path d="M2.99969 2.5H8.9997V8.5" stroke="#6A6E6E" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M8.99951 2.5L1.99951 9.5" stroke="#6A6E6E" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

const PowerUpIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
  <rect width="12" height="12" fill="white"/>
  <path d="M9.10712 4.83461H5.99997V0.950684L0.950867 7.16497H4.4464V11.0489L9.10712 4.83461Z" stroke="#6A6E6E" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
